import React, { useEffect, useState, useRef, useMemo } from "react";
import "./createQuestion.scss";
import CreateReadynessQuestion from "./CreateReadynessQuestion";
import DropDownPopup from "./DropDown/DropDown";
import { toast } from "react-toastify";
import Header from "../../layout/header/Header";
import axiosInstance from "../../../httpInterceptor";
import Tooltip from "./ToolTip/Tooltip";
import { FaArrowLeft } from "react-icons/fa";
import { IoIosCopy } from "react-icons/io";
import { saveAs } from "file-saver";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MdDragIndicator } from "react-icons/md";

// Sortable Row Component for drag and drop
const SortableRow: React.FC<{ id: string; children: React.ReactNode; className?: string; backgroundColor?: string }> = ({ id, children, className, backgroundColor }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  
  const [isHoveringHandle, setIsHoveringHandle] = useState(false);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? transition : `${transition}, box-shadow 0.2s ease, transform 0.2s ease`,
    opacity: isDragging ? 0.6 : 1,
    backgroundColor: isDragging ? '#e0f2fe' : (backgroundColor || 'transparent'),
    boxShadow: isDragging ? '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
    cursor: isDragging ? 'grabbing' : 'default',
    zIndex: isDragging ? 1000 : 'auto',
    position: isDragging ? 'relative' : 'static',
  };

  return (
    <tr ref={setNodeRef} style={style} className={className} {...attributes}>
      <td style={{ width: "3%", textAlign: "center", verticalAlign: "middle", padding: "8px 4px" }}>
        <div 
          {...listeners} 
          style={{ 
            cursor: isDragging ? 'grabbing' : 'grab',
            display: "inline-flex", 
            alignItems: "center",
            padding: "4px",
            borderRadius: "4px",
            transition: "all 0.2s ease",
            backgroundColor: isHoveringHandle ? '#dbeafe' : 'transparent',
            transform: isHoveringHandle && !isDragging ? 'scale(1.1)' : 'scale(1)',
          }}
          onMouseEnter={() => setIsHoveringHandle(true)}
          onMouseLeave={() => setIsHoveringHandle(false)}
          onMouseDown={() => setIsHoveringHandle(false)}
          title="Drag to reorder"
        >
          <MdDragIndicator 
            style={{ 
              fontSize: "18px", 
              color: isHoveringHandle ? "#3b82f6" : "#9ca3af",
              transition: "color 0.2s ease",
            }} 
          />
        </div>
      </td>
      {children}
    </tr>
  );
};

type Question = {
  _id?: number;
  description: string;
  set: string;
  category: string;
  type: "text" | "dropdown" | "radio" | "checkbox" | null;
  values?: { option: string; response: string }[];
  isRequire?: boolean;
  weightage?: number;
  weightageCategory?: number;
};

// Radial Progress Bar Component (inline)
const RadialProgressBar: React.FC<{
  percentage: number;
  size?: number;
  strokeWidth?: number;
}> = ({ percentage, size = 50, strokeWidth = 4 }) => {
  const validPercentage =
    typeof percentage === "number" && !isNaN(percentage) ? percentage : 0;

  const radius = (size - strokeWidth) / 2;
  const center = size / 2;

  const getColor = () => {
    switch (true) {
      case validPercentage > 100:
        return "#a91313ff"; // Red for >100%
      case validPercentage === 100:
        return "#0dff66ff"; // Green for 100%
      case validPercentage >= 80:
        return "#f3c37bff"; // Yellowish for 80-99%
      default:
        return "#081008ff"; // Dark for <80%
    }
  };

  const polarToCartesian = (
    cx: number,
    cy: number,
    r: number,
    angleDeg: number
  ) => {
    const angleRad = (angleDeg * Math.PI) / 180;
    return {
      x: cx + r * Math.cos(angleRad),
      y: cy + r * Math.sin(angleRad),
    };
  };

  let arcPath = "";
  let showArc = validPercentage > 0;

  if (validPercentage === 100) {
    arcPath = `
      M ${center} ${center - radius}
      A ${radius} ${radius} 0 1 1 ${center} ${center + radius}
      A ${radius} ${radius} 0 1 1 ${center} ${center - radius}
    `;
  } else if (validPercentage > 0) {
    const maxArcAngle = 340;
    const angle = Math.min((validPercentage / 100) * maxArcAngle, maxArcAngle);
    const startAngle = -90;
    const endAngle = startAngle + angle;

    const start = polarToCartesian(center, center, radius, startAngle);
    const end = polarToCartesian(center, center, radius, endAngle);
    const largeArcFlag = angle > 180 ? 1 : 0;

    arcPath = `
      M ${start.x} ${start.y}
      A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}
    `;
  }

  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <svg width={size} height={size}>
        {/* Background Circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke="#e0e0e0"
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        {/* Progress Arc (only if > 0%) */}
        {showArc && (
          <path
            d={arcPath}
            stroke={getColor()}
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
          />
        )}
      </svg>
      {/* Percentage Text */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          fontSize: "10px",
          color: getColor(),
        }}
      >
        {validPercentage.toFixed(1)}%
      </div>
    </div>
  );
};

const CreateQuestion2: React.FC = () => {
  const [showReadiness, setShowReadiness] = useState(false);
  const [questionText, setQuestionText] = useState("");
  const [setText, setSetText] = useState("");
  const [viewTab, setViewTab] = useState(true);

  const [category, setCategory] = useState("");
  const [responseType, setResponseType] = useState<
    "text" | "dropdown" | "radio" | "checkbox" | null
  >(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isOpen, setIsOpen] = useState<{ [key: string]: Boolean }>({});
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [showDropDownPopup, setShowDropDownPopup] = useState(false);
  const [savedItems, setSavedItems] = useState<{ response: string }[]>([]);
  const [tempSavedItems, setTempSavedItems] = useState<{ response: string }[]>(
    []
  );
  const [inputType, setInputType] = useState("");
  const [tempInputType, setTempInputType] = useState("");
  const [hasUserTyped, setHasUserTyped] = useState(false);
  const [hasUserTypedCategory, setHasUserTypedCategory] = useState(false);
  const [mandatory, setMandatory] = useState(false);
  const [weightageEnabled, setWeightageEnabled] = useState(false);
  const [weightageValue, setWeightageValue] = useState<number | "">(""); // Allow number or empty string for input
  const [weightageError, setWeightageError] = useState<string>("");
  const weightageInputRef = useRef<HTMLInputElement>(null);
  const [isWeightageFocused, setIsWeightageFocused] = useState(false);
  const [showWeightagePreview, setShowWeightagePreview] = useState(false);
  const previewListRef = useRef<HTMLDivElement | null>(null);
  const [hoverPreview, setHoverPreview] = useState<null | { text: string; weight: number; x: number; y: number }>(null);
  const weightageContainerRef = useRef<HTMLDivElement | null>(null);
  const [previewPlacement, setPreviewPlacement] = useState<'right' | 'left' | 'overlay'>('right');
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [questionToCopy, setQuestionToCopy] = useState<Question | null>(null);
  const [collapsedSets, setCollapsedSets] = useState<{ [key: string]: boolean }>({});
  const [categoryWeightages, setCategoryWeightages] = useState<{
    [key: string]: number;
  }>({});
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [categoryWeightageEnabled, setCategoryWeightageEnabled] =
    useState(false);
  const [categoryWeightageValue, setCategoryWeightageValue] = useState<
    number | ""
  >(""); // Allow number or empty string for input
  
  // State to track question order for each set+category combination
  const [questionOrder, setQuestionOrder] = useState<{ [key: string]: string[] }>({});

  // Refs for click-outside detection
  const categoryInputRef = useRef<HTMLInputElement>(null);
  const setInputRef = useRef<HTMLInputElement>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const setDropdownRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Close category suggestions if click is outside both input and dropdown
      if (
        categoryInputRef.current &&
        !categoryInputRef.current.contains(target) &&
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(target)
      ) {
        setShowCategorySuggestions(false);
      }
      
      // Close set suggestions if click is outside both input and dropdown
      if (
        setInputRef.current &&
        !setInputRef.current.contains(target) &&
        setDropdownRef.current &&
        !setDropdownRef.current.contains(target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Function to calculate total weightage for a category within a specific set
  const calculateCategoryWeightage = (setName: string, categoryName: string): number => {
    if (!setName) return 0;
    const total = questions
      .filter((q) => q.set === setName && q.category === categoryName)
      .reduce((sum, q) => sum + (q.weightage || 0), 0);

    return typeof total === "number" && !isNaN(total) ? total : 0;
  };

  // Function to update category weightages (scoped by set + category)
  const updateCategoryWeightages = () => {
    const weightages: { [key: string]: number } = {};
    // groupedArray has shape { set: { category: [questions] } }
    Object.keys(groupedArray).forEach((setName) => {
      Object.keys(groupedArray[setName]).forEach((category) => {
        const key = `${setName}||${category}`; // composite key
        weightages[key] = calculateCategoryWeightage(setName, category);
      });
    });
    setCategoryWeightages(weightages);
  };

  // Drag and drop sensors with activation constraint for better UX
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end event - key format: "setName||categoryName"
  const handleDragEnd = (event: DragEndEvent, setName: string, categoryName: string) => {
    const { active, over } = event;
    const key = `${setName}||${categoryName}`;

    if (over && active.id !== over.id) {
      setQuestionOrder((prevOrder) => {
        const categoryQuestions = prevOrder[key] || [];
        const oldIndex = categoryQuestions.indexOf(active.id as string);
        const newIndex = categoryQuestions.indexOf(over.id as string);
        const newOrder = arrayMove(categoryQuestions, oldIndex, newIndex);
        
        const updatedOrder = {
          ...prevOrder,
          [key]: newOrder,
        };
        
        // Save to localStorage
        localStorage.setItem('question_order_create', JSON.stringify(updatedOrder));
        
        return updatedOrder;
      });
    }
  };

  // Get sorted questions for a category based on questionOrder state
  const getSortedQuestions = (setName: string, categoryName: string, questions: Question[]) => {
    const key = `${setName}||${categoryName}`;
    if (!questionOrder[key] || questionOrder[key].length === 0) {
      return questions;
    }

    const orderMap = new Map(questionOrder[key].map((id, index) => [id, index]));
    
    return [...questions].sort((a, b) => {
      const idA = a._id?.toString() || '';
      const idB = b._id?.toString() || '';
      const indexA = orderMap.get(idA) ?? Infinity;
      const indexB = orderMap.get(idB) ?? Infinity;
      return indexA - indexB;
    });
  };

  const handleExportSet = async (setName: string) => {
    try {
      const url = `${process.env.REACT_APP_QUESTION_SERVICE_BASE_URL}/questions-export-by-set/${encodeURIComponent(
        setName
      )}`;

      const response = await axiosInstance.get(url, { responseType: "arraybuffer" });
      const contentType =
        response.headers["content-type"] ||
        response.headers["Content-Type"] ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      // Detect whether we received raw XLSX bytes (PK..) or a base64 string (UEsD..)
      let data = new Uint8Array(response.data as ArrayBuffer);
      let blobSource: Uint8Array | ArrayBuffer = data;

      const isZipHeader =
        data.length >= 4 &&
        data[0] === 0x50 &&
        data[1] === 0x4b &&
        data[2] === 0x03 &&
        data[3] === 0x04; // "PK\x03\x04"

      if (!isZipHeader) {
        const text = new TextDecoder("utf-8").decode(data).trim();
        if (text.startsWith("UEsD")) {
          // API Gateway returned base64 text; decode to binary
          const binaryString = window.atob(text);
          const len = binaryString.length;
          const bytes = new Uint8Array(len);
          for (let i = 0; i < len; i += 1) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          blobSource = bytes;
        }
      }

      const blobData = blobSource instanceof Uint8Array ? blobSource : new Uint8Array(blobSource as ArrayBuffer);
      const blob = new Blob([new Uint8Array(blobData)], {
        type: contentType,
      });

      const datePart = new Date().toISOString().slice(0, 10);
      const safeSet = setName.replace(/[^A-Za-z0-9]+/g, "_");
      const filename = `questions-template-${safeSet}-${datePart}.xlsx`;

      saveAs(blob, filename);
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error("Failed to export questions template for set", error);
      if (error?.response?.status === 404) {
        toast.error("No questions found for this assessment type to export");
      } else {
        toast.error("Failed to export questions template. Please try again.");
      }
    }
  };

  const handleExportCategory = async (setName: string, categoryName: string) => {
    try {
      const url = `${process.env.REACT_APP_QUESTION_SERVICE_BASE_URL}/questions-export/${encodeURIComponent(
        setName
      )}/${encodeURIComponent(categoryName)}`;

      const response = await axiosInstance.get(url, { responseType: "arraybuffer" });

      const contentType =
        response.headers["content-type"] ||
        response.headers["Content-Type"] ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      let data = new Uint8Array(response.data as ArrayBuffer);
      let blobSource: Uint8Array | ArrayBuffer = data;

      const isZipHeader =
        data.length >= 4 &&
        data[0] === 0x50 &&
        data[1] === 0x4b &&
        data[2] === 0x03 &&
        data[3] === 0x04;

      if (!isZipHeader) {
        const text = new TextDecoder("utf-8").decode(data).trim();
        if (text.startsWith("UEsD")) {
          const binaryString = window.atob(text);
          const len = binaryString.length;
          const bytes = new Uint8Array(len);
          for (let i = 0; i < len; i += 1) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          blobSource = bytes;
        }
      }

      const blob = new Blob([new Uint8Array(blobSource instanceof Uint8Array ? blobSource : new Uint8Array(blobSource as ArrayBuffer))], {
        type: contentType,
      });

      const datePart = new Date().toISOString().slice(0, 10);
      const safeSet = setName.replace(/[^A-Za-z0-9]+/g, "_");
      const safeCategory = categoryName.replace(/[^A-Za-z0-9]+/g, "_");
      const filename = `questions-${safeSet}-${safeCategory}-${datePart}.xlsx`;

      saveAs(blob, filename);
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error("Failed to export questions for category", error);
      if (error?.response?.status === 404) {
        toast.error("No questions found for this category to export");
      } else {
        toast.error("Failed to export questions. Please try again.");
      }
    }
  };

  // Validate weightage before adding/updating question
  const validateWeightage = async (
    newWeightage: number,
    categoryName: string,
    setName?: string,
    excludeQuestionId?: number
  ): Promise<boolean> => {
    // Use provided setName or current `setText`
    const effectiveSet = (setName || setText || "").toString();
    const currentCategoryWeightage = calculateCategoryWeightage(
      effectiveSet,
      categoryName
    );

    let adjustedCurrentWeightage = currentCategoryWeightage;
    if (excludeQuestionId) {
      const existingQuestion = questions.find(
        (q) => q._id === excludeQuestionId
      );
      if (existingQuestion) {
        adjustedCurrentWeightage -= existingQuestion.weightage || 0;
      }
    }

    const totalWeightage = adjustedCurrentWeightage + newWeightage;

    if (totalWeightage > 100) {
      toast.error(
        `Total weightage for category "${categoryName}" would exceed 100%. Current: ${adjustedCurrentWeightage.toFixed(
          1
        )}%, Attempting to add: ${newWeightage}%`
      );
      return false;
    }

    return true;
  };

  // Questions in current set+category (for interactive preview)
  const questionsInSection = useMemo(() => {
    if (!setText || !category) return [] as Question[];
    // Try to use groupedArray if it has the set/category to preserve original order
    try {
      const setKey = setText.toString();
      const catKey = category.toString();
      if (groupedArray[setKey] && groupedArray[setKey][catKey]) {
        return groupedArray[setKey][catKey];
      }
    } catch (err) {
      // fall back to filtering the flat questions list
    }

    return questions.filter(
      (q: Question) =>
        q.set?.toString().toUpperCase() === setText.toString().toUpperCase() &&
        q.category?.toString().toUpperCase() === category.toString().toUpperCase()
    );
  }, [questions, setText, category]);

  const otherQuestions = useMemo(() => {
    return questionsInSection.filter((q: Question) => q._id !== editingIndex);
  }, [questionsInSection, editingIndex]);

  // Auto-scroll preview list when it opens or when editingIndex changes
  useEffect(() => {
    if (!showWeightagePreview) return;
    const container = previewListRef.current;
    if (!container) return;

    // Try to find the element corresponding to editingIndex
    if (editingIndex !== null) {
      const el = container.querySelector(`[data-qid="${editingIndex}"]`) as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }

    // Otherwise, scroll to top when opening
    container.scrollTo({ top: 0, behavior: 'smooth' });
  }, [showWeightagePreview, editingIndex]);

  // Responsively choose placement on window resize/scroll
  useEffect(() => {
    const computePlacement = () => {
      const btnRect = weightageContainerRef.current?.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      if (!btnRect) return;

      // prefer right if there is enough space, otherwise left; on very small screens use overlay centered
      const spaceRight = viewportWidth - btnRect.right;
      const spaceLeft = btnRect.left;
      if (viewportWidth < 520) {
        setPreviewPlacement('overlay');
      } else if (spaceRight > 360) {
        setPreviewPlacement('right');
      } else if (spaceLeft > 360) {
        setPreviewPlacement('left');
      } else {
        setPreviewPlacement('overlay');
      }
    };

    computePlacement();
    window.addEventListener('resize', computePlacement);
    window.addEventListener('scroll', computePlacement, true);
    return () => {
      window.removeEventListener('resize', computePlacement);
      window.removeEventListener('scroll', computePlacement, true);
    };
  }, [showWeightagePreview]);

  // Close the weightage preview when clicking outside of its container
  useEffect(() => {
    if (!showWeightagePreview) return;

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      // If click is outside the preview panel and the toggle button/container, close it
      const previewEl = previewListRef.current;
      const containerEl = weightageContainerRef.current;

      if (
        previewEl && !previewEl.contains(target) &&
        containerEl && !containerEl.contains(target)
      ) {
        setShowWeightagePreview(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showWeightagePreview]);

  const otherTotal = useMemo(() => {
    return otherQuestions.reduce((s: number, q: Question) => s + (q.weightage || 0), 0);
  }, [otherQuestions]);

  //fetching all the question from database......
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_QUESTION_SERVICE_BASE_URL}/questions`
        );
        // Exclude only records explicitly marked as deleted
        const activeQuestions = response.data.filter(
          (q: any) => q.isDeleted !== true
        );
        setQuestions(activeQuestions);
      } catch (error) {
        toast.error("Error fetching questions");
      }
    };

    fetchQuestions();
  }, []);

  // Update weightages when questions change
  useEffect(() => {
    updateCategoryWeightages();
  }, [questions]);

  // Load question order from localStorage on mount
  useEffect(() => {
    const savedOrder = localStorage.getItem('question_order_create');
    if (savedOrder) {
      setQuestionOrder(JSON.parse(savedOrder));
    }
  }, []);

  // Initialize question order when questions are loaded
  useEffect(() => {
    if (questions.length > 0) {
      const grouped = groupArray(questions);
      
      setQuestionOrder((prevOrder) => {
        const newOrder: { [key: string]: string[] } = {};
        
        Object.keys(grouped).forEach((setName) => {
          Object.keys(grouped[setName]).forEach((categoryName) => {
            const key = `${setName}||${categoryName}`;
            const currentIds = grouped[setName][categoryName].map((q: Question) => q._id?.toString() || '');
            
            if (prevOrder[key]) {
              // Use existing order and add any new questions
              const existingIds = new Set(prevOrder[key]);
              const newIds = currentIds.filter((id: string) => !existingIds.has(id));
              newOrder[key] = [...prevOrder[key].filter((id: string) => currentIds.includes(id)), ...newIds];
            } else {
              // Create initial order
              newOrder[key] = currentIds;
            }
          });
        });
        
        return newOrder;
      });
    }
  }, [questions]);

  //adding question to the database on click of add question button
  const addQuestion = async () => {
    if (questionText.trim() === "" || !category || !setText) {
      return;
    }

    // Validate weightage if enabled
    if (weightageEnabled) {
      const weightageNum = Number(weightageValue);
      const isValid = await validateWeightage(
        weightageNum,
        category.toUpperCase(),
        setText.toUpperCase(),
        editingIndex || undefined
      );
      if (!isValid) {
        return;
      }
    }

    const newQuestion: Question = {
      description: questionText,
      set: setText,
      category: category,
      type: responseType,
      isRequire: mandatory,
      weightage: weightageEnabled ? Number(weightageValue) : 0,
      weightageCategory: categoryWeightageEnabled
        ? Number(categoryWeightageValue)
        : undefined,
      values:
        responseType !== "text"
          ? savedItems.map((item, index) => {
              // For dropdown, split response into text and score
              // For radio/checkbox, use response as-is
              if (responseType === "dropdown" && item.response.includes("|")) {
                const [resp, score] = item.response
                  .split("|")
                  .map((s) => s.trim());
                return {
                  option: (index + 1).toString(),
                  response: resp,
                  score: score ? Number(score) : 0,
                };
              } else {
                // For radio and checkbox, no score
                return {
                  option: (index + 1).toString(),
                  response: item.response.trim(),
                  score: 0,
                };
              }
            })
          : [{ option: "1", response: "", score: 0 }],
    };
    try {
      if (editingIndex !== null) {
        const response = await axiosInstance.put(
          `${process.env.REACT_APP_QUESTION_SERVICE_BASE_URL}/questions/${editingIndex}`,
          newQuestion
        );
        const updatedQuestions = [...questions];
        const currentUpdatedQuestionIndex = updatedQuestions.findIndex(
          (question) => question._id === editingIndex
        );

        updatedQuestions[currentUpdatedQuestionIndex] = response.data;
        setQuestions(updatedQuestions);
        setEditingIndex(null);
        setQuestionText("");
        setCategory("");
        setResponseType(null);
        setSavedItems([]);
        setTempSavedItems([]);
        toast.success("Question edited successfully");
      } else {
        const response = await axiosInstance.post(
          `${process.env.REACT_APP_QUESTION_SERVICE_BASE_URL}/questions`,
          newQuestion
        );
        setQuestions([...questions, response.data[0]]);
        console.log(response.data[0]);
        toast.success("Question added successfully");
      }

      setQuestionText("");
      setCategory("");
      setResponseType(null);
      setSavedItems([]);
      setTempSavedItems([]);
      setMandatory(false);
      setWeightageEnabled(false);
      setWeightageValue("");
      setShowWeightagePreview(false);
    } catch (error: any) {
      if (
        error.response?.status === 400 &&
        error.response?.data?.error?.includes("weightage exceeds 100%")
      ) {
        toast.error(error.response.data.error);
      } else {
        toast.error("Error adding/updating questions");
      }
      return;
    }
    setSetText("");
    // After successful save, make sure we show the list (Questionnaires menu)
    setShowUnsavedDialog(false);
    setViewTab(true);
  };

  const editQuestion = async (index: number, preserveWeightageToggle = false) => {
    setIsEditMode(true);
    const { data } = await axiosInstance.get(
      `${process.env.REACT_APP_QUESTION_SERVICE_BASE_URL}/questions/${index}`
    );
    setQuestionText(data.description);
    setSetText(data.set);
    setCategory(data.category);
    setResponseType(data.type);
    setInputType(data.type);
    setTempInputType(data.type);
    setMandatory(data.isRequire ?? false);
    if (!preserveWeightageToggle) {
      setWeightageEnabled(!!data.weightage);
    }
    setWeightageValue(data.weightage || "");
    setCategoryWeightageEnabled(!!data.weightageCategory);
    setCategoryWeightageValue(
      data.weightageCategory !== undefined ? data.weightageCategory : ""
    );
    const resArr = data.values.map((item: { response: string; score?: number }) => {
      // Reconstruct the response|score format for dropdown items only
      if (data.type === "dropdown" && item.score !== undefined) {
        return { response: `${item.response}|${item.score}` };
      }
      // For radio and checkbox, just return the response as-is
      return { response: item.response };
    });
    setTempSavedItems(resArr);
    setSavedItems(resArr); // Initialize savedItems with the data as well
    setEditingIndex(index);
    setViewTab(false);
  };

  const cancelEdit = () => {
    setQuestionText("");
    setSetText("");
    setIsEditMode(false);
    setCategory("");
    setResponseType(null);
    setEditingIndex(null);
    setTempSavedItems([]);
    setSavedItems([]);
    setMandatory(false);
    setWeightageEnabled(false);
    setWeightageValue("");
    setShowWeightagePreview(false);
    setShowUnsavedDialog(false);
  };

  const deleteQuestion = async (id: string) => {
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_QUESTION_SERVICE_BASE_URL}/questions/${id}`
      );
      const updatedQuestions = questions.filter(
        (question) => question._id?.toString() !== id
      );
      setQuestions(updatedQuestions);
      toast.success("Question deleted successfully");
    } catch (error) {
      toast.error("Error deleting the question");
    }
  };

  const [showModal, setShowModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  const handleDeleteClick = (itemId: string) => {
    setItemToDelete(itemId);
    setShowModal(true);
  };

  const handleConfirmDelete = () => {
    if (itemToDelete !== null) {
      deleteQuestion(itemToDelete);
      setShowModal(false);
    }
  };

  const handleCopyClick = (question: Question) => {
    setQuestionToCopy(question);
    setShowCopyModal(true);
    toast.success("Question copied successfully");
  };

  // const handleCopyTo = (setName: string, categoryName: string) => {
  //   if (!questionToCopy) return;
  //   const duplicate = questions.find(
  //     (q) =>
  //       q.description === questionToCopy.description &&
  //       q.set === setName &&
  //       q.category === categoryName &&
  //       q.type === questionToCopy.type
  //   );
  //   if (duplicate) {
  //     toast.warning(
  //       "A question with the same content, questionnaire, category, and response type already exists."
  //     );
  //     return;
  //   }
  //   const newQuestion: Question = {
  //     ...questionToCopy,
  //     set: setName.toUpperCase(), // <-- Ensure uppercase
  //     category: categoryName.toUpperCase(), // <-- Ensure uppercase
  //     _id: undefined,
  //     values: questionToCopy.values?.map((item) => {
  //       const [resp, score] = item.response.split("|").map((s) => s.trim());
  //       return {
  //         ...item,
  //         response: resp,
  //         score: score ? Number(score) : 0,
  //       };
  //     }),
  //   };
  //   axiosInstance
  //     .post(
  //       `${process.env.REACT_APP_QUESTION_SERVICE_BASE_URL}/questions`,
  //       newQuestion
  //     )
  //     .then((response) => {
  //       setQuestions([...questions, response.data[0]]);
  //       toast.success("Question paste successfully");
  //       setShowCopyModal(false);
  //       setQuestionToCopy(null);
  //     })
  //     .catch(() => {
  //       toast.error("Error copying & pasting question");
  //     });
  // };
  const toggleSetCollapse = (setName: string) => {
    setCollapsedSets((prev) => ({
      ...prev,
      [setName]: !prev[setName],
    }));
  };

  const handleCopyTo = (setName: string, categoryName: string) => {
    if (!questionToCopy) return;

    const newQuestion = {
      description: questionToCopy.description,
      category: categoryName.toUpperCase(),
      set: setName.toUpperCase(),
      type: questionToCopy.type,
      values: questionToCopy.values?.map((item) => {
        const [resp, score] = item.response.split("|").map((s) => s.trim());
        return {
          option: item.option,
          response: resp,
          score: score ? Number(score) : 0,
        };
      }),
      isRequire: questionToCopy.isRequire ?? false,
      weightage: 0,
      modifiedDate: new Date(),
    };
    axiosInstance
      .post(
        `${process.env.REACT_APP_QUESTION_SERVICE_BASE_URL}/questions`,
        newQuestion
      )
      .then((response) => {
        setQuestions([...questions, response.data[0]]);
        toast.success("Question pasted successfully");
        setShowCopyModal(false);
        setQuestionToCopy(null);
      })
      .catch((error) => {
        if (error.response?.data?.error) {
          toast.error(error.response.data.error);
        } else {
          toast.error("Error copying & pasting question");
        }
      });
  };
  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleResponseTypeChange = (
    responseType: "text" | "dropdown" | "radio" | "checkbox"
  ) => {
    setResponseType(responseType);
    // When switching response type
    if (responseType !== "text") {
      // If switching to a type that matches tempInputType, restore tempSavedItems
      if (responseType === tempInputType && tempSavedItems.length > 0) {
        setSavedItems(tempSavedItems);
        setInputType(responseType);
      } 
      // If switching to a different type, clear saved items
      else if (responseType !== inputType) {
        setSavedItems([]);
        setInputType(responseType);
      }
      // If it's the same as current inputType, keep savedItems as is
      else {
        setInputType(responseType);
      }
    }
  };

  const toggleDropDownPopup = (type: string, valueArray: any) => {
    // If switching to a different input type, clear saved items
    if (type !== tempInputType) {
      setSavedItems([]);
    } else if (isEditMode && tempInputType === type) {
      // In edit mode, if we're reopening the same type, always load from tempSavedItems
      // This ensures data persists when user reopens the dropdown
      if (tempSavedItems.length > 0) {
        setSavedItems(tempSavedItems);
      }
    }

    setInputType(type);

    setShowDropDownPopup((prev) => !prev);
  };

  const handleSave = (items: { response: string }[]) => {
    setSavedItems(items);
    setTempSavedItems(items); // Also update tempSavedItems to ensure data persists
    setTempInputType(inputType);
    setShowDropDownPopup(false);
  };

  const handleClose = () => {
    setShowDropDownPopup(false);
  };

  const handleOpenClose = (category: string) => {
    setIsOpen((prevState) => ({
      ...prevState,
      [category]: !prevState[category],
    }));
    cancelEdit();
  };

  const groupQuestionsByCategory = (questions: any) => {
    return questions.reduce((acc: any, question: any) => {
      const category = question.category;
      if (!acc[category]) acc[category] = [];
      acc[category].push(question);
      return acc;
    }, {});
  };
  const groupQuestionsBySet = (questions: any) => {
    return questions.reduce((acc: any, question: any) => {
      const set = question.set;
      if (!acc[set]) acc[set] = [];
      acc[set].push(question);
      return acc;
    }, {});
  };

  const groupArray = (questions: any) => {
    return questions.reduce((result: any, obj: any) => {
      const { set, category } = obj;

      if (!result[set]) {
        result[set] = {};
      }
      if (!result[set][category]) {
        result[set][category] = [];
      }
      result[set][category].push(obj);
      return result;
    }, {});
  };

  const groupedArray = groupArray(questions);

  const groupedQuestionsBySet = groupQuestionsBySet(questions);
  const groupedQuestionsByCategory = groupQuestionsByCategory(questions);
 
  const suggestions = Object.keys(groupedQuestionsBySet);
  const noSuggestionsMessage: string =
    "No matching set name found! Create a new one!";

  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  useEffect(() => {
    if (hasUserTyped && setText.trim().length >= 2) {
      const filtered = suggestions.filter((suggestion) =>
        suggestion.toLowerCase().includes(setText.toLowerCase())
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    }
  }, [setText, hasUserTyped]);

  // When the copy modal opens, default all Sets to collapsed (closed)
  useEffect(() => {
    if (showCopyModal) {
      const initialCollapsed: { [key: string]: boolean } = {};
      Object.keys(groupedArray || {}).forEach((setName) => {
        initialCollapsed[setName] = true;
      });
      setCollapsedSets(initialCollapsed);
    }
  }, [showCopyModal]);

  const handleClick = (suggestion: string) => {
    setSetText(suggestion);
    setFilteredSuggestions([]);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e: { key: string }) => {
    if (e.key === "Enter") {
      setSetText(filteredSuggestions[activeSuggestionIndex]);
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    } else if (e.key === "ArrowUp") {
      if (activeSuggestionIndex === 0) return;
      setActiveSuggestionIndex(activeSuggestionIndex - 1);
    } else if (e.key === "ArrowDown") {
      if (activeSuggestionIndex === filteredSuggestions.length - 1) return;
      setActiveSuggestionIndex(activeSuggestionIndex + 1);
    }
  };
  /////////////Logic for Category suggestions
  const categorySuggestions = Object.keys(groupedQuestionsByCategory);
  console.log(categorySuggestions);
  const noCategorySuggestionsMessage: string =
    "No matching category name found! Create a new one!";

  const [filteredCategorySuggestions, setFilteredCategorySuggestions] =
    useState<string[]>([]);
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  const [activeCategorySuggestionIndex, setActiveCategorySuggestionIndex] =
    useState(0);
  useEffect(() => {
    if (hasUserTypedCategory && category && category.trim().length >= 2 && setText) {
      const filtered = (
        groupedArray[setText] ? Object.keys(groupedArray[setText]) : []
      ).filter((suggestion) =>
        suggestion.toLowerCase().includes(category.toLowerCase())
      );
      setFilteredCategorySuggestions(filtered);
      setShowCategorySuggestions(true);
    } else {
      setFilteredCategorySuggestions([]);
      setShowCategorySuggestions(false);
    }
  }, [category, setText, hasUserTypedCategory]);

  const handleCategoryClick = (suggestion: string) => {
    setCategory(suggestion);
    setShowCategorySuggestions(false);
    setFilteredCategorySuggestions([]);
  };

  const handleCategoryKeyDown = (e: { key: string }) => {
    if (e.key === "Enter") {
      setCategory(filteredCategorySuggestions[activeCategorySuggestionIndex]);
      setFilteredCategorySuggestions([]);
      setShowCategorySuggestions(false);
    } else if (e.key === "ArrowUp") {
      if (activeCategorySuggestionIndex === 0) return;
      setActiveCategorySuggestionIndex(activeCategorySuggestionIndex - 1);
    } else if (e.key === "ArrowDown") {
      if (
        activeCategorySuggestionIndex ===
        filteredCategorySuggestions.length - 1
      )
        return;
      setActiveCategorySuggestionIndex(activeCategorySuggestionIndex + 1);
    }
  };

  // Returns the sum of weightageCategory for all questions in a category (for a given set)
  const getTotalCategoryWeightage = (set: string, category: string) => {
    if (!groupedArray[set] || !groupedArray[set][category]) return 0;
    return groupedArray[set][category].reduce(
      (sum: number, q: any) => sum + (q.weightageCategory || 0),
      0
    );
  };

  return (
    <div className="app">
      <Header fromProjectReadiness={showReadiness} />
      {/* Question type toggle */}
      <div className="question-type-toggle">
        <span
          className={`toggle-label${!showReadiness ? ' toggle-label--active' : ''}`}
          onClick={() => setShowReadiness(false)}
        >
          Project Assessment Question
        </span>
        <div
          className={`toggle-switch${showReadiness ? ' toggle-switch--on' : ''}`}
          onClick={() => setShowReadiness((prev) => !prev)}
          role="switch"
          aria-checked={showReadiness}
        >
          <div className="toggle-thumb" />
        </div>
        <span
          className={`toggle-label${showReadiness ? ' toggle-label--active' : ''}`}
          onClick={() => setShowReadiness(true)}
        >
          Project Readiness Question
        </span>
      </div>

      {showReadiness ? (
        <CreateReadynessQuestion hideHeader={true} />
      ) : (
        <>
      {!viewTab ? (
        <div className="newQues-box">
          <p className="text1">
            {editingIndex !== null ? "Edit Question" : "Create A New Question"}
          </p>
          <div>
            <br />
            <label>
              Add New Assessment Type / Select Existing Assessment Type:
            </label>
            <Tooltip elements={suggestions} />
            <br />
            <input
              ref={setInputRef}
              className="textfield"
              type="text"
              value={setText}
              onChange={(e) => {
                setSetText(e.target.value);
                setHasUserTyped(true);
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (setText.trim().length >= 2 && hasUserTyped) {
                  setShowSuggestions(true);
                }
              }}
            />
            <div className="dropDown" ref={setDropdownRef}>
              {showSuggestions && setText && (
                <ul>
                  {filteredSuggestions.length ? (
                    filteredSuggestions.map((suggestion, index) => (
                      <li
                        key={suggestion}
                        onClick={() => handleClick(suggestion)}
                        className={
                          index === activeSuggestionIndex ? "active" : ""
                        }
                      >
                        {suggestion}
                      </li>
                    ))
                  ) : (
                    <li className="noSuggMsg">{noSuggestionsMessage}</li>
                  )}
                </ul>
              )}
            </div>
          </div>
          <div>
            <label>Add New Category / Select Existing Category:</label>
            <Tooltip elements={suggestions} />

            <br />
            <input
              ref={categoryInputRef}
              className="textfield"
              type="text"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setHasUserTypedCategory(true);
              }}
              onFocus={() => {
                if (category && category.trim().length >= 2 && hasUserTypedCategory) {
                  setShowCategorySuggestions(true);
                }
              }}
              onKeyDown={handleCategoryKeyDown}
            />
            <div className="dropDown" ref={categoryDropdownRef}>
              {showCategorySuggestions && category && (
                <ul>
                  {filteredCategorySuggestions.length ? (
                    filteredCategorySuggestions.map((suggestion, index) => (
                      <li
                        key={suggestion}
                        onClick={() => handleCategoryClick(suggestion)}
                        className={
                          index === activeCategorySuggestionIndex
                            ? "active"
                            : ""
                        }
                      >
                        {suggestion}
                      </li>
                    ))
                  ) : (
                    <li className="noSuggMsg">
                      {noCategorySuggestionsMessage}
                    </li>
                  )}
                </ul>
              )}
            </div>
          </div>
          <div>
            <label>Question Text: </label>
            <br />
            <input
              className="textfield"
              type="text"
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
            />
          </div>
          <div
            style={{ position: "relative", right: "2%" }}
            className="response-options"
          >
            <label style={{fontWeight:'bold'}}>Response Type: </label>
            <div>
              <label style={{ cursor: "pointer" }}>
                <input
                  type="Radio"
                  value="text"
                  checked={responseType === "text"}
                  onChange={() => handleResponseTypeChange("text")}
                />
                <span 
                  style={{ 
                    color: responseType === "text" ? "#000000" : "#4D4D4D", 
                    fontWeight: "bold",
                    transition: "all 0.3s ease",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = "#000000"}
                  onMouseLeave={(e) => e.currentTarget.style.color = responseType === "text" ? "#000000" : "#4D4D4D"}
                >
                  Text
                </span>
              </label>
              <label style={{ cursor: "pointer" }}>
                <input
                  type="Radio"
                  value="dropdown"
                  checked={responseType === "dropdown"}
                  onChange={(e) =>
                    handleResponseTypeChange(
                      e.target.value as
                        | "text"
                        | "dropdown"
                        | "radio"
                        | "checkbox"
                    )
                  }
                />
                <span 
                  style={{ 
                    color: responseType === "dropdown" ? "#000000" : "#4D4D4D", 
                    fontWeight: "bold",
                    transition: "all 0.3s ease",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = "#000000"}
                  onMouseLeave={(e) => e.currentTarget.style.color = responseType === "dropdown" ? "#000000" : "#4D4D4D"}
                >
                  Dropdown
                </span>
                {responseType === "dropdown" && (
                  <>
                    {savedItems.length > 0 && (
                      <select
                        value=""
                        onChange={(e) => {
                          // Prevent selection, reset to default
                          e.target.value = "";
                        }}
                        style={{
                          marginLeft: "10px",
                          padding: "5px 10px",
                          border: "1px solid #FF5E4F",
                          borderRadius: "4px",
                          fontSize: "13px",
                          backgroundColor: "#e8f0ff",
                          cursor: "pointer",
                          color: "#333",
                          fontFamily: "monospace",
                          minWidth: "160px",
                          translate:'0px 1px',
                          height: "33px",
                          boxSizing: "border-box",
                          width:'120px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                        title="View added options" 
                      >
                        <option value="">📋 View Options ({savedItems.length})</option>
                        {savedItems.map((item, idx) => {
                          // Split response by pipe to get text and score
                          const parts = item.response.split("|");
                          const text = parts[0]?.trim() || item.response;
                          const score = parts[1]?.trim() || "";
                          
                          // Format with padding for alignment
                          const displayText = score 
                            ? `${idx + 1}. Text: ${text.padEnd(20, " ")}  ,  Score: ${score}`
                            : `${idx + 1}. ${item.response}`;
                          
                          return (
                            <option key={idx} value={item.response}>
                              {displayText}
                            </option>
                          );
                        })}
                      </select>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDropDownPopup("dropdown", savedItems);
                      }}
                      style={{
                        marginLeft: "10px",
                        padding: "5px 10px",
                        border: "1px solid #0d5eff",
                        borderRadius: "4px",
                        fontSize: "13px",
                        background: "linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)",
                        cursor: "pointer",
                        color: "#fff",
                        fontWeight: "500",
                        height: "33px",
                        boxSizing: "border-box"
                      }}
                      title="Configure options"
                    >
                      ⚙️ Configure Option
                    </button>
                  </>
                )}
              </label>
              <label style={{ cursor: "pointer" }}>
                <input
                  type="Radio"
                  value="radio"
                  checked={responseType === "radio"}
                  onChange={(e) =>
                    handleResponseTypeChange(
                      e.target.value as
                        | "text"
                        | "dropdown"
                        | "radio"
                        | "checkbox"
                    )
                  }
                />
                <span 
                  style={{ 
                    color: responseType === "radio" ? "#000000" : "#4D4D4D", 
                    fontWeight: "bold",
                    transition: "all 0.3s ease",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = "#000000"}
                  onMouseLeave={(e) => e.currentTarget.style.color = responseType === "radio" ? "#000000" : "#4D4D4D"}
                >
                  Radio
                </span>
                {responseType === "radio" && (
                  <>
                    {savedItems.length > 0 && (
                      <select
                        value=""
                        onChange={(e) => {
                          // Prevent selection, reset to default
                          e.target.value = "";
                        }}
                        style={{
                          marginLeft: "10px",
                          padding: "5px 10px",
                          border: "1px solid #FF5E4F",
                          borderRadius: "4px",
                          fontSize: "13px",
                          backgroundColor: "#e8f0ff",
                          cursor: "pointer",
                          color: "#333",
                          fontFamily: "monospace",
                          minWidth: "160px",
                          translate:'0px 1px',
                          height: "33px",
                          boxSizing: "border-box",
                          width:'120px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                        title="View added options"
                      >
                        <option value="">📋 View Options ({savedItems.length})</option>
                        {savedItems.map((item, idx) => (
                          <option key={idx} value={item.response}>
                            {idx + 1}. {item.response}
                          </option>
                        ))}
                      </select>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDropDownPopup("radio", savedItems);
                      }}
                      style={{
                        marginLeft: "10px",
                        padding: "5px 10px",
                        border: "1px solid #0d5eff",
                        borderRadius: "4px",
                        fontSize: "13px",
                        background: "linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)",
                        cursor: "pointer",
                        color: "#fff",
                        fontWeight: "500",
                        height: "33px",
                        boxSizing: "border-box"
                      }}
                      title="Configure options"
                    >
                      ⚙️ Configure Option
                    </button>
                  </>
                )}
              </label>
              <label style={{ cursor: "pointer" }}>
                <input
                  type="Radio"
                  value="checkbox"
                  checked={responseType === "checkbox"}
                  onChange={(e) =>
                    handleResponseTypeChange(
                      e.target.value as
                        | "text"
                        | "dropdown"
                        | "radio"
                        | "checkbox"
                    )
                  }
                />
                <span 
                  style={{ 
                    color: responseType === "checkbox" ? "#000000" : "#4D4D4D", 
                    fontWeight: "bold",
                    transition: "all 0.3s ease",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = "#000000"}
                  onMouseLeave={(e) => e.currentTarget.style.color = responseType === "checkbox" ? "#000000" : "#4D4D4D"}
                >
                  CheckBox
                </span>
                {responseType === "checkbox" && (
                  <>
                    {savedItems.length > 0 && (
                      <select
                        value=""
                        onChange={(e) => {
                          // Prevent selection, reset to default
                          e.target.value = "";
                        }}
                        style={{
                          marginLeft: "10px",
                          padding: "5px 10px",
                          border: "1px solid #FF5E4F",
                          borderRadius: "4px",
                          fontSize: "13px",
                          backgroundColor: "#e8f0ff",
                          cursor: "pointer",
                          color: "#333",
                          fontFamily: "monospace",
                          minWidth: "160px",
                          translate:'0px 1px',
                          height: "33px",
                          boxSizing: "border-box",
                          width:'120px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                        title="View added options"
                      >
                        <option value="">📋 View Options ({savedItems.length})</option>
                        {savedItems.map((item, idx) => (
                          <option key={idx} value={item.response}>
                            {idx + 1}. {item.response}
                          </option>
                        ))}
                      </select>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDropDownPopup("checkbox", savedItems);
                      }}
                      style={{
                        marginLeft: "10px",
                        padding: "5px 10px",
                        border: "1px solid #0d5eff",
                        borderRadius: "4px",
                        fontSize: "13px",
                        background: "linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)",
                        cursor: "pointer",
                        color: "#fff",
                        fontWeight: "500",
                        height: "33px",
                        boxSizing: "border-box"
                      }}
                      title="Configure options"
                    >
                      ⚙️ Configure Option
                    </button>
                  </>
                )}
              </label>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "20px",
              marginBottom: "10px",
              fontSize: "14px",
            }}
          >
            <div>
              <label>Mandatory:</label>
              <input
                type="checkbox"
                checked={mandatory}
                onChange={(e) => setMandatory(e.target.checked)}
                style={{ position: "relative", top: "2px" }}
              />
            </div>
              <div ref={weightageContainerRef} style={{ display: "flex", alignItems: "flex-start", gap: "12px", position: 'relative' }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <label>Weightage(in %):</label>
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "pointer",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        position: "relative",
                        width: "44px",
                        height: "24px",
                        background: weightageEnabled ? "linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)" : "#ccc",
                        borderRadius: "12px",
                        transition: "background-color 0.3s",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: "2px",
                          left: weightageEnabled ? "22px" : "2px",
                          width: "20px",
                          height: "20px",
                          backgroundColor: "white",
                          borderRadius: "50%",
                          transition: "left 0.3s",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                        }}
                        onClick={(e) => { e.stopPropagation(); setWeightageEnabled(!weightageEnabled); }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                      />
                    </div>
                    {weightageEnabled && (
                      <TextField
                        inputRef={weightageInputRef}
                        onFocus={() => setIsWeightageFocused(true)}
                        type="number"
                        value={weightageValue}
                        onChange={async (e) => {
                          const raw = e.target.value;
                          const parsed = raw === "" ? "" : Number(raw);
                          setWeightageValue(parsed);

                          // immediate validation while typing
                          if (raw === "") {
                            setWeightageError("");
                            return;
                          }
                          const num = Number(parsed);
                          if (isNaN(num) || num < 0 || num > 100) {
                            setWeightageError("Please enter a number between 0 and 100.");
                            return;
                          }

                          // Live validation: check against other questions in section
                          if (category) {
                            await validateWeightage(
                              num,
                              category.toUpperCase(),
                              setText.toUpperCase(),
                              editingIndex || undefined
                            );
                          }

                          setWeightageError("");
                        }}
                        onBlur={async () => {
                          // onBlur keep same validation (ensure final value ok)
                          if (weightageValue === "") {
                            setWeightageError("");
                            return;
                          }
                          const num = Number(weightageValue);
                          if (isNaN(num) || num < 0 || num > 100) {
                            setWeightageError("Please enter a number between 0 and 100.");
                            return;
                          }
                          if (category) {
                            const isValid = await validateWeightage(
                              num,
                              category.toUpperCase(),
                              setText.toUpperCase(),
                              editingIndex || undefined
                            );
                            if (!isValid) {
                              setWeightageError("This weightage would make the category total exceed 100%.");
                              return;
                            }
                          }
                          setWeightageError("");
                          setIsWeightageFocused(false);
                        }}
                        error={!!weightageError}
                        FormHelperTextProps={{ style: { whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'unset', minHeight: 22, marginTop: -7, display: 'inline-block', transform: 'translateY(8px)',translate:'-14px'} }}
                        helperText={weightageError || ' '}
                        placeholder="Enter weightage"
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                        size="small"
                        variant="outlined"
                        sx={{
                          width: '135px',
                          marginLeft: "5px",
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            height: '34px',
                            padding: 0,
                            '& fieldset': {
                              borderColor: '#FF5E4F'
                            },
                            '&:hover fieldset': {
                              borderColor: '#FF5E4F'
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#FF5E4F'
                            },
                          },
                          '& .MuiOutlinedInput-input': {
                            padding: '6px 8px',
                            fontSize: '14px',
                            height: 'auto',
                            backgroundColor:'#fff'
                          },
                        }}
                      />
                    )}
                  </label>
                </div>

                {/* Toggle button to explicitly open preview */}
                {weightageEnabled && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => setShowWeightagePreview((s) => !s)}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)')}
                      style={{
                        background: 'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)',
                        color: '#fff',
                        border: '1px solid rgba(11,37,64,0.08)',
                        padding: '6px 10px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontSize: 13,
                        minWidth: 140,
                        textAlign: 'center'
                      }}
                      title={showWeightagePreview ? 'Hide section preview' : 'Show section preview'}
                    >
                      {showWeightagePreview ? 'Hide list' : 'Show section list'}
                    </button>
                  </div>
                )}

                {/* Interactive preview panel for other questions' weightages */}
                {(weightageEnabled && showWeightagePreview) && (
                  <div style={{
                    position: 'absolute',
                    top: previewPlacement === 'overlay' ? '36px' : '6px',
                    left: previewPlacement === 'right' ? 'calc(100% + 12px)' : (previewPlacement === 'left' ? 'auto' : '50%'),
                    right: previewPlacement === 'left' ? 'calc(100% + 12px)' : 'auto',
                    transform: previewPlacement === 'overlay' ? 'translateX(-50%)' : 'none',
                    zIndex: 999,
                    minWidth: 280,
                    maxWidth: 340,
                    background: "linear-gradient(180deg,#fff,#fbfcff)",
                    border: "1px solid rgba(13,94,255,0.12)",
                    borderRadius: 12,
                    padding: "12px",
                    boxShadow: "0 10px 30px rgba(9,36,80,0.08)",
                    fontSize: 13,
                    color: "#0b2540",
                    overflow: 'hidden',
                    translate:'0px -7px'
                  }} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                    <div style={{ fontWeight: 700, marginBottom: 8, color: '#072f57', fontSize: 14 }}>Section weightages</div>
                    <div ref={previewListRef} style={{ maxHeight: 120, overflowY: "auto", marginBottom: 6, position: 'relative', paddingBottom: 18 }}>
                      {questionsInSection.length === 0 ? (
                        <div style={{ color: "#6b7785", padding: '4px 2px', fontSize: 12 }}>No questions in this section.</div>
                      ) : (
                        <>
                          {editingIndex !== null && (() => {
                            const current = questionsInSection.find((qq: any) => qq._id === editingIndex);
                            return current ? (
                              <div
                                data-qid={current._id}
                                onMouseEnter={(e) => {
                                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                  setHoverPreview({ text: current.description || '', weight: current.weightage || 0, x: rect.right, y: rect.top + rect.height / 2 });
                                }}
                                onMouseMove={(e) => {
                                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                  setHoverPreview((s) => s ? { ...s, x: rect.right, y: rect.top + rect.height / 2 } : s);
                                }}
                                onMouseLeave={() => setHoverPreview(null)}
                                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 6px', borderBottom: otherQuestions.length > 0 ? "1px dashed rgba(13,94,255,0.04)" : 'none', gap: 6 }}
                              >
                                  <div style={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#168a2a', fontSize: 12, fontWeight: 700 }}>{current.description}</div>
                                  <div style={{ fontWeight: 800, marginLeft: 8, color: '#168a2a', minWidth: 36, textAlign: 'right', fontSize: 12 }}>{(current.weightage ?? 0).toFixed(0)}%</div>
                                </div>
                            ) : null;
                          })()}
                          {otherQuestions.map((q:any, i:any) => (
                            <div
                              key={q._id ?? i}
                              data-qid={q._id}
                              onMouseEnter={(e) => {
                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                setHoverPreview({ text: q.description || '', weight: q.weightage || 0, x: rect.right, y: rect.top + rect.height / 2 });
                              }}
                              onMouseMove={(e) => {
                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                setHoverPreview((s) => s ? { ...s, x: rect.right, y: rect.top + rect.height / 2 } : s);
                              }}
                              onMouseLeave={() => setHoverPreview(null)}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: 'center',
                                padding: "6px 6px",
                                borderBottom: i < otherQuestions.length - 1 ? "1px dashed rgba(13,94,255,0.04)" : 'none',
                                cursor: 'default',
                                transition: 'background 0.12s ease',
                                gap: 6
                              }}
                            >
                              <div style={{ maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: '#0b2540', fontSize: 12 }}>{q.description}</div>
                              <div style={{ fontWeight: 700, marginLeft: 8, color: '#072f57', minWidth: 36, textAlign: 'right', fontSize: 12 }}>{(q.weightage ?? 0).toFixed(0)}%</div>
                            </div>
                          ))}
                        </>
                      )}
                      {/* bottom fade to visually cut the list */}
                      {/* Fade overlay to indicate more content when scrollable */}
                      <div style={{ position: 'absolute', left: 0, right: 8, bottom: 0, height: 14, pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(255,255,255,0), rgba(255,255,255,0.96))' }} />
                    </div>
                    <div style={{ paddingTop: 6, borderTop: '1px solid rgba(9,36,80,0.03)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ color: '#6b7785', fontSize: 12 }}>Remaining</div>
                        <div style={{ fontWeight: 900, color: otherTotal + (Number(weightageValue) || 0) > 100 ? '#a91313' : '#072f57' }}>{Math.max(0, (100 - otherTotal - (Number(weightageValue) || 0))).toFixed(1)}%</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Hover preview tooltip - rendered at document level */}
                {hoverPreview && (
                  (() => {
                    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
                    const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
                    const tooltipW = 360;
                    const tooltipH = 160; // estimated
                    // Prefer showing to the right of the hovered item, otherwise left
                    const spaceRight = vw - hoverPreview.x;
                    const offset = 12; // gap from hovered item
                    const left = spaceRight > tooltipW + offset ? Math.min(vw - tooltipW - 8, hoverPreview.x + offset) : Math.max(8, hoverPreview.x - tooltipW - offset);
                    const top = Math.min(Math.max(8, hoverPreview.y - tooltipH / 2), vh - 8 - tooltipH);
                    return (
                      <div style={{ position: 'fixed', left, top, zIndex: 9999, pointerEvents: 'none', background: '#fff', border: '1px solid rgba(9,36,80,0.12)', padding: '8px 10px', borderRadius: 8, boxShadow: '0 8px 32px rgba(2,12,27,0.12)', maxWidth: tooltipW, minWidth: 220, maxHeight: 220, overflow: 'auto' }}>
                        <div style={{ fontSize: 13, color: '#072f57', fontWeight: 700, marginBottom: 6 }}>Question</div>
                        <div style={{ fontSize: 13, color: '#0b2540', whiteSpace: 'normal', lineHeight: 1.4, marginBottom: 8 }}>{hoverPreview.text}</div>
                        <div style={{ fontSize: 12, color: '#6b7785', paddingTop: 6, borderTop: '1px solid rgba(9,36,80,0.06)' }}>Weightage: <span style={{ fontWeight: 800, color: '#072f57', fontSize: 13 }}>{hoverPreview.weight.toFixed(1)}%</span></div>
                      </div>
                    );
                  })()
                )}
              </div>
          </div>
          <button
            className="Addbox"
            onClick={addQuestion}
            disabled={questionText.trim() === ""}
          >
            {editingIndex !== null ? "Save Question" : "Add Question +"}
          </button>
          {showDropDownPopup && (
            <DropDownPopup
              onSave={handleSave}
              savedItems={savedItems}
              onClose={handleClose}
              inputType={inputType}
              rowEdit={[]}
            />
          )}
        </div>
      ) : (
        <div className="set-section">
          {Object.keys(groupedArray).length > 0 &&
            Object.keys(groupedArray).map((set, index) => (
              <div key={index}>
                <div
                  className="questionbtn"
                  style={{
                    borderTopLeftRadius: index === 0 ? "4px" : "0px",
                    borderTopRightRadius: index === 0 ? "4px" : "0px",
                    borderBottomLeftRadius:
                      index === Object.keys(groupedArray).length - 1
                        ? "4px"
                        : "0px",
                    borderBottomRightRadius:
                      index === Object.keys(groupedArray).length - 1
                        ? "4px"
                        : "0px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingRight: "1rem",
                  }}
                  onClick={() => handleOpenClose(set)}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <p style={{ color: "black", margin: 0, fontWeight: "650" }}>
                      ASSESSMENT TYPE - {set.toUpperCase()}
                    </p>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExportSet(set);
                      }}
                      style={{
                        textTransform: "none",
                        borderRadius: 8,
                        padding: "4px 10px",
                        fontSize: 12,
                        background:
                          "linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)",
                        boxShadow:
                          "0 4px 12px rgba(11,116,255,0.18)",
                      }}
                    >
                      Download
                    </Button>
                    {isOpen[set] ? (
                      <i
                        className="fa-solid fa-chevron-down"
                        style={{ color: "black" }}
                      ></i>
                    ) : (
                      <i
                        className="fa-solid fa-chevron-right"
                        style={{ color: "black" }}
                      ></i>
                    )}
                  </div>
                </div>
                {isOpen[set] && (
                  <>
                    {Object.keys(groupedArray[set]).map((category, index) => (
                      <div key={index}>
                        <div
                          className="questionBtnn"
                          onClick={() =>
                            handleOpenClose(`${set} - ${category}`)
                          }
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "1rem",
                          }}
                        >
                          <div
                            className="category-weightage-scroll"
                            style={{
                              overflowX: "auto",
                              whiteSpace: "nowrap",
                              width: "100%",
                            }}
                          >
                            {/* Left section: category, progress, and weightage */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                minWidth: "600px",
                              }}
                            >
                              <p
                                style={{
                                  color: "white",
                                  margin: 0,
                                  fontSize: "1rem",
                                  fontWeight: 500,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                CATEGORY - {category.toUpperCase()}
                              </p>
                              <p  style={{
                                  color: "white",
                                  fontSize: "1rem",
                                  fontWeight: 500,
                                  whiteSpace: "nowrap",
                                }}>Total Question Weightage:</p>
                              <RadialProgressBar
                                percentage={categoryWeightages[`${set}||${category}`] || 0}
                                size={40}
                                strokeWidth={4}
                              />
                              {(categoryWeightages[`${set}||${category}`] || 0) < 100 && (
                                <div
                                  title="Section-wise total must be 100%"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    background: 'linear-gradient(180deg, #FFF3E0 0%, #FFE0B2 100%)',
                                    color: '#5A3E00',
                                    padding: '6px 10px',
                                    borderRadius: 18,
                                    fontWeight: 600,
                                    fontSize: 12,
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                                    marginLeft: 8,
                                  }}
                                >
                                  <span style={{ fontSize: 14, lineHeight: 1 }} aria-hidden>
                                    ⚠️
                                  </span>
                                  <span>Section-wise Weightage should be 100%</span>
                                </div>
                              )}
                              {/* Categorical Weightage Radial Progress Bar */}
                              {/* <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center", // Center horizontally
                                  width: "100%", // Take full width of parent flex
                                  gap: "8px",
                                  marginLeft: "20rem",
                                }}
                              > */}
                                {/* <span
                                  style={{
                                    color: "#fbffe3ff",
                                    fontWeight: 500,
                                    fontSize: "1rem",
                                    marginRight: 0,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Category Weightage Overview:
                                </span>
                                <RadialProgressBar
                                  percentage={getTotalCategoryWeightage(
                                    set,
                                    category
                                  )}
                                  size={40}
                                  strokeWidth={4}
                                /> */}
                              {/* </div> */}
                            </div>
                          </div>
                          {/* Right section: dropdown arrow */}
                          <div style={{ marginLeft: "auto" }}>
                            {isOpen[`${set} - ${category}`] ? (
                              <i
                                style={{ color: "white" }}
                                className="fa-solid fa-chevron-down"
                              ></i>
                            ) : (
                              <i
                                style={{ color: "white" }}
                                className="fa-solid fa-chevron-right"
                              ></i>
                            )}
                          </div>
                        </div>
                        {isOpen[`${set} - ${category}`] && (
                          <div className="responsive-table-wrapper">
                            <table className="question-table">
                              <thead className="head">
                                <tr>
                                  <td style={{ width: "3%", textAlign: "center" }}>#</td>
                                  <td>Questions</td>
                                  <td style={{whiteSpace:'nowrap'}}>Response Type</td>
                                  <td>Mandatory</td>
                                  <td>Weightage</td>
                                  <td>Modify</td>
                                  <td>Delete</td>
                                  <td
                                    style={{
                                      textAlign: "center",
                                      verticalAlign: "middle",
                                      minWidth: 120,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "1rem",
                                        color: "rgb(60, 60, 60)",
                                      }}
                                    >
                                      Copy Question
                                    </span>
                                  </td>
                                </tr>
                              </thead>
                              <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={(event: DragEndEvent) => handleDragEnd(event, set, category)}
                              >
                                <SortableContext
                                  items={getSortedQuestions(set, category, groupedQuestionsBySet[set].filter((question: any) => question.category === category)).map((q: any) => q._id.toString())}
                                  strategy={verticalListSortingStrategy}
                                >
                                  <tbody>
                                    {getSortedQuestions(set, category, groupedQuestionsBySet[set].filter((question: any) => question.category === category))
                                      .map((question: any, idx: any) => (
                                        <SortableRow 
                                          key={question._id.toString()} 
                                          id={question._id.toString()} 
                                          className="table-row"
                                          backgroundColor={idx % 2 === 0 ? '#ebebeb' : '#d7d8da'}
                                        >
                                          <td>{idx + 1}. {question.description}</td>
                                      <td
                                        style={{ translate: "5px" }}
                                        className="response-options"
                                      >
                                        {question.type.toUpperCase()}
                                      </td>
                                      <td>
                                        {question.isRequire ? "Yes" : "No"}
                                      </td>
                                      <td>
                                        {question.weightage
                                          ? `${question.weightage}%`
                                          : "N/A"}
                                      </td>
                                      <td>
                                        {editingIndex === question._id ? (
                                          <button
                                            onClick={cancelEdit}
                                            className="saveicon"
                                            style={{
                                              backgroundColor: "transparent",
                                            }}
                                          >
                                            <img
                                              src={require("../../../images/cross.png")}
                                              alt=""
                                              style={{ color: "#000000" }}
                                            />
                                          </button>
                                        ) : (
                                          <button
                                            className="modifyDelIcon"
                                            style={{
                                              backgroundColor: "transparent",
                                            }}
                                            onClick={() =>
                                              editQuestion(question._id)
                                            }
                                          >
                                            <img
                                              src={require("../../../images/modify.png")}
                                              alt=""
                                            />
                                          </button>
                                        )}
                                      </td>
                                      <td>
                                        <button
                                          className="modifyDelIcon"
                                          style={{
                                            backgroundColor: "transparent",
                                            cursor: "pointer",
                                          }}
                                          onClick={() =>
                                            handleDeleteClick(question._id)
                                          }
                                        >
                                          <img
                                            style={{
                                              backgroundColor: "transparent",
                                            }}
                                            src={require("../../../images/delete.png")}
                                            alt="Soft delete question"
                                          />
                                        </button>
                                        <Dialog
                                          open={showModal}
                                          onClose={handleCloseModal}
                                          slotProps={{
                                            backdrop: {
                                              style: {
                                                backgroundColor: "rgba(99, 97, 97, 0.35)",
                                                backdropFilter: "blur(-3px)",
                                                WebkitBackdropFilter: "blur(-2px)",
                                              },
                                            },
                                          }}
                                          PaperProps={{
                                            style: {
                                              width: 320,
                                              borderRadius: 10,
                                              padding: 0,
                                              boxShadow: "0 10px 30px rgba(80, 81, 84, 0.12)",
                                            },
                                          }}
                                        >
                                          <DialogTitle
                                            style={{
                                              fontSize: "1rem",
                                              fontWeight: 700,
                                              color: "#072A49",
                                              padding: "16px 18px",
                                              borderBottom: "1px solid #eef2f6",
                                            }}
                                          >
                                            Confirm Deletion
                                          </DialogTitle>
                                          <DialogContent
                                            style={{ padding: "12px 18px 6px 18px" }}
                                          >
                                            <DialogContentText
                                              style={{
                                                fontSize: "0.95rem",
                                                color: "#374151",
                                              }}
                                            >
                                              Are you sure you want to delete this question? This action cannot be undone.
                                            </DialogContentText>
                                          </DialogContent>
                                          <DialogActions style={{ padding: "10px 14px 14px 14px", justifyContent: "flex-end" }}>
                                            <Button
                                              onClick={handleCloseModal}
                                              variant="outlined"
                                              style={{
                                                textTransform: "none",
                                                borderRadius: 8,
                                                padding: "8px 12px",
                                                background: "linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)",
                                                color: "#fff",
                                              }}
                                            >
                                              No
                                            </Button>
                                            <Button
                                              onClick={handleConfirmDelete}
                                              variant="contained"
                                              style={{
                                                background: "linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)",
                                                color: "#fff",
                                                textTransform: "none",
                                                borderRadius: 8,
                                                padding: "8px 12px",
                                                marginLeft: 8,
                                              }}
                                            >
                                              Yes, delete
                                            </Button>
                                          </DialogActions>
                                        </Dialog>
                                      </td>
                                      <td
                                        style={{
                                          textAlign: "center",
                                          verticalAlign: "middle",
                                          minWidth: 120,
                                        }}
                                      >
                                        <Button
                                          variant="contained"
                                          
                                          size="small"
                                          style={{
                                            textTransform: "none",
                                            borderRadius: 8,
                                            fontWeight: 600,
                                            minWidth: 90,
                                            boxShadow:
                                              "0 2px 8px rgba(25, 118, 210, 0.08)",
                                            padding: "4px 0",
                                            color: "#fff",
                                            transition: "background 0.2s",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background:"linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)"
                                          }}
                                          onClick={() =>
                                            handleCopyClick(question)
                                          }
                                          startIcon={<IoIosCopy />}
                                        >
                                          Copy
                                        </Button>
                                      </td>
                                    </SortableRow>
                                  ))}
                                  </tbody>
                                </SortableContext>
                              </DndContext>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            ))}
        </div>
      )}
      <button
        className="switchTab"
        data-viewtab={viewTab}
        onClick={() => {
          if (viewTab) {
            setViewTab(!viewTab);
          } else {
            const isDirty =
              questionText.trim() !== "" ||
              setText !== "" ||
              category !== "" ||
              responseType !== null ||
              savedItems.length > 0 ||
              tempSavedItems.length > 0 ||
              mandatory ||
              weightageEnabled ||
              weightageValue !== "" ||
              categoryWeightageEnabled ||
              categoryWeightageValue !== "";

            if (isDirty) {
              setShowUnsavedDialog(true);
            } else {
              setViewTab(!viewTab);
            }
          }
        }}
      >
        {viewTab ? (
          "+ New Question"
        ) : (
          <>
            <FaArrowLeft style={{ translate: "-10px 2px" }} /> Back
          </>
        )}
      </button>

      <Dialog
        open={showUnsavedDialog}
        onClose={() => setShowUnsavedDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            borderRadius: 12,
            padding: 0,
            boxShadow: "0 12px 40px rgba(15,23,42,0.12)",
            overflow: "hidden",
            background: "#ffffff",
          },
        }}
      >
        <DialogTitle
          style={{
            fontWeight: 700,
            fontSize: "1.125rem",
            padding: "20px 24px 8px",
            color: "linear-gradient(135deg, #FF5E4F 0%, #E54536 100%)",
            borderBottom: "1px solid #eef2f6",
          }}
        >
          Unsaved changes
        </DialogTitle>
        <DialogContent style={{ padding: "18px 24px 6px 24px" }}>
          <DialogContentText style={{ color: "#4b5563", fontSize: 14, lineHeight: 1.5 }}>
            Unless you explicitly press the Save Question button, your changes
            will not be saved. What would you like to do?
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ justifyContent: "flex-end", padding: "14px 20px 20px 20px" }}>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                await addQuestion();
              } catch (e) {
                return;
              }
              setShowUnsavedDialog(false);
            }}
            style={{
              background: "linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)",
              color: "#fff",
              boxShadow: "0 8px 28px rgba(13,94,255,0.14)",
              borderRadius: 10,
              padding: "10px 14px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              minWidth: 140,
            }}
          >
            Save and go back
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setQuestionText("");
              setSetText("");
              setCategory("");
              setResponseType(null);
              setSavedItems([]);
              setTempSavedItems([]);
              setMandatory(false);
              setWeightageEnabled(false);
              setWeightageValue("");
              setCategoryWeightageEnabled(false);
              setCategoryWeightageValue("");
              setIsEditMode(false);
              setEditingIndex(null);
              setShowUnsavedDialog(false);
              setViewTab(true);
            }}
            style={{
             background: "linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)",
              color: "#fff",
              boxShadow: "0 8px 28px rgba(13,94,255,0.14)",
              borderRadius: 10,
              padding: "10px 14px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              minWidth: 140,
            }}
          >
            Discard changes
          </Button>
          <Button
            onClick={() => setShowUnsavedDialog(false)}
            style={{
             background: "linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)",
              color: "#fff",
              boxShadow: "0 8px 28px rgba(13,94,255,0.14)",
              borderRadius: 10,
              padding: "10px 14px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              minWidth: 140,
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
      {!showReadiness && showCopyModal && ( 
        <Dialog
          open={showCopyModal}
          onClose={() => setShowCopyModal(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            style: {
              borderRadius: 12,
              padding: 0,
              boxShadow: "0 12px 40px rgba(2,12,27,0.08)",
              background: "#fff",
            },
          }}
        >
          <DialogTitle
            style={{
              fontWeight: 700,
              fontSize: "1.125rem",
              padding: "18px 24px 6px 24px",
              color: "black",
              borderBottom: "1px solid #eef2f6",
            }}
          >
            Copy Question To
          </DialogTitle>
          <DialogContent style={{ padding: "12px 20px 8px 20px" }}>
            <div style={{ maxHeight: "340px", overflowY: "auto",scrollbarWidth:'thin' }}>
              {Object.keys(groupedArray).map((setName) => {
                const categories = Object.keys(groupedArray[setName]);
                const filteredCategories = categories.filter((categoryName) => {
                  if (!questionToCopy) return false;
                  return true;
                });
                if (filteredCategories.length === 0) return null;
                return (
                  <div
                    key={setName}
                    style={{
                      marginBottom: 18,
                      borderBottom: "1px solid #eee",
                      paddingBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 500,
                        color: "black",
                        fontSize: "1.1rem",
                        marginBottom: 4,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        width: "100%",
                      }}
                      onClick={() => toggleSetCollapse(setName)}
                    >
                      <div>
                        Assessment Type:{" "}
                        <span style={{ color: "#FF5E4F", marginLeft: "4px" }}>{setName}</span>
                      </div>
                      <i
                        className={collapsedSets[setName] ? "fa-solid fa-chevron-right" : "fa-solid fa-chevron-down"}
                        style={{
                          marginLeft: "8px",
                          fontSize: "0.9rem",
                          color: "black",
                        }}
                      ></i>
                    </div>
                    {!collapsedSets[setName] && filteredCategories.map((categoryName) => (
                      <div
                        key={categoryName}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginLeft: 12,
                          marginBottom: 8,
                          background: "#f5f7fa",
                          borderRadius: 8,
                          padding: "8px 12px",
                        }}
                      >
                        <span style={{ fontWeight: 400, color: "#333" }}>
                          Category:{" "}
                          <span style={{ color: "#FF5E4F" }}>
                            {categoryName}
                          </span>
                        </span>
                        <Button
                          variant="contained"
                          // color="primary"
                          size="small"
                          style={{
                            textTransform: "none",
                            borderRadius: 6,
                            fontWeight: 500,
                            boxShadow: "none",
                            background:'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)'
                          }}
                          onClick={() => handleCopyTo(setName, categoryName)}
                        >
                          Paste Here
                        </Button>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </DialogContent>
          <DialogActions style={{ justifyContent: "flex-end", padding: "12px 20px 18px 20px" }}>
            <Button
              onClick={() => setShowCopyModal(false)}
              variant="contained"
              color="primary"
              // size="small"
              style={{
                textTransform: "none",
                borderRadius: 6,
                fontWeight: 500,
                boxShadow: "none",
                padding: "8px 14px",
                background:'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)'
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default CreateQuestion2;
